import { IGoal } from "motw_core/Entity/IGoal";
import { IGoalDataPoint } from "motw_core/Entity/IGoalDataPoint";
import { EGoalDatapointType } from "motw_core/Goals/GoalDataPoints/EGoalDatapointType";
import {
    FilterDatapointsForGoal,
    IGoalWithDatapoints,
} from "motw_core/Repositories/DatapointRepository";
import { CalculateDaysLeft } from "source/app/presentation/MainPanel/Goals/GoalHelpers/CalculateDaysLeft";
import { MapGoalDirection } from "source/app/presentation/MainPanel/Goals/GoalHelpers/GetFilteredGoals";
import { IGoalStreakInfoDTO } from "motw_core/DTOs/GoalStreakInfo/IGoalStreakInfoDTO";
import { minimumLogInputEntryTextLength } from "source/app/presentation/MainPanel/Log/LogInputForm/LogInputEntryText/LogInputEntryText";
import { GetIsFilteringGoalCards } from "source/app/presentation/MainPanel/Log/LogInputForm/LogInputForm";

export function GetProcessedGoals(
    goals: IGoal[],
    goalDataPoints: IGoalDataPoint[],
    searchText: string,
    selectedDirection: string,
    showHiddenGoals: boolean,
    showArchivedGoals: boolean,
    goalStreakInfo: IGoalStreakInfoDTO[],
    clickedGoalId?: number
): IGoal[] {
    if (!GetIsFilteringGoalCards()) {
        return goals;
    }

    if (searchText.length === 0) {
        return [];
    }

    if (
        searchText.length > 0 &&
        searchText.length < minimumLogInputEntryTextLength
    ) {
        return goals;
    }

    const filteredGoals = Filter(
        goals,
        searchText,
        selectedDirection,
        showHiddenGoals,
        showArchivedGoals,
        clickedGoalId
    );

    const goalsWithDatapoints = AttachDatapoints(filteredGoals, goalDataPoints);
    const goalsWithDaysLeft = AddDaysLeft(goalsWithDatapoints);
    const sortedGoals = Sort(goalsWithDaysLeft, goalStreakInfo);

    return sortedGoals;
}

function Sort(
    goals: IGoalWithDatapoints[],
    goalStreakInfo: IGoalStreakInfoDTO[]
) {
    const sortedGoals = goals.sort(
        (a, b) =>
            GetRealMomentum(a, goalStreakInfo) -
            GetRealMomentum(b, goalStreakInfo)
    );

    return sortedGoals;
}

function GetRealMomentum(
    goal: IGoalWithDatapoints,
    goalStreakInfo: IGoalStreakInfoDTO[]
) {
    const streakInfo = goalStreakInfo.find(
        (gsi) => gsi.result_goalid === goal.id
    );

    return streakInfo?.realMomentum ?? 0;
}

function Filter(
    goals: IGoal[],
    searchText: string,
    selectedDirection: string,
    showHiddenGoals: boolean,
    showArchivedGoals: boolean,
    clickedGoalId?: number
): IGoal[] {
    if (clickedGoalId !== undefined) {
        return goals.filter((goal) => goal.id === clickedGoalId);
    }

    const isSearchMoreThanOneWord = searchText.split(" ").length > 1;

    if (isSearchMoreThanOneWord) {
        return goals;
    }

    if (goals.length === 0) {
        return goals;
    }

    const isUserSearching = searchText !== "";

    const filteredGoals = goals
        .filter((goal) =>
            searchText
                ? goal.name
                      .toLowerCase()
                      .includes(searchText.toLowerCase().trim())
                : goal
        )
        .filter((goal) => {
            if (isUserSearching) {
                // If user is searching for a goal, don't filter by goal direction
                return goal;
            } else {
                const direction = MapGoalDirection(selectedDirection);
                return direction !== undefined
                    ? goal.isupper === direction
                    : goal;
            }
        })
        .filter((goal) => {
            if (isUserSearching) {
                // If user is searching for a goal, don't hide daily goals
                return goal;
            } else {
                return !goal.isdaily;
            }
        })
        .filter((goal) =>
            showHiddenGoals || isUserSearching ? goal : goal.istracking
        )
        .filter((goal) =>
            showArchivedGoals || isUserSearching ? goal : !goal.isarchived
        );

    return filteredGoals;
}

function AttachDatapoints(
    goals: IGoal[],
    datapoints: IGoalDataPoint[]
): IGoalWithDatapoints[] {
    const goalsWithDatapoints: IGoalWithDatapoints[] = goals.map(
        (goal: IGoal) =>
            Object.assign({}, goal, {
                ...goal,
                datapoints: FilterDatapointsForGoal(
                    goal,
                    datapoints,
                    EGoalDatapointType.GoalDatapoint
                ),
            })
    );

    return goalsWithDatapoints;
}

function AddDaysLeft(goals: IGoalWithDatapoints[]) {
    const goalsWithDaysLeft: IGoalWithDatapoints[] = goals.map(
        (goal: IGoalWithDatapoints) => UpdateGoalWithDaysLeft(goal)
    );

    return goalsWithDaysLeft;
}

function UpdateGoalWithDaysLeft(goal: IGoalWithDatapoints) {
    const daysleft = CalculateDaysLeft(goal);
    const updatedGoal: IGoalWithDatapoints = { ...goal, daysleft };
    return updatedGoal;
}
